<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import ErrorState from "@/views/shared/components/ErrorState.vue";
import { subscriptionsApi, type ProductSubscription } from "@/services/subscriptions";
import api, { ConnectionError } from "@/services/api";
import { useRecentlyViewed } from "@/composables/useRecentlyViewed";

const route = useRoute();
const router = useRouter();
const { trackView } = useRecentlyViewed();

const loading = ref(false);
const hasError = ref(false);
const errorType = ref<'connection' | 'http' | 'general'>('general');
const subscription = ref<ProductSubscription | null>(null);
const orgDetails = ref<any>(null);
const productDetails = ref<any>(null);
const showEditModal = ref(false);
const showRemoveModal = ref(false);
const editForm = ref({
  startDate: "",
  endDate: "",
});

const subscriptionId = computed(() => route.params.subscriptionId as string);

onMounted(async () => {
  await loadSubscriberDetails();
});

async function loadSubscriberDetails() {
  loading.value = true;
  hasError.value = false;
  try {
    // Fetch subscription details
    const response = await api.get(`/subscriptions/${subscriptionId.value}`);
    if (!response.ok) {
      throw new Error("Failed to load subscription");
    }
    subscription.value = await response.json();

    // Fetch organization details
    const orgResponse = await api.get(`/org/${subscription.value!.orgId}`);
    if (orgResponse.ok) {
      orgDetails.value = await orgResponse.json();
    }

    // Fetch product details
    const productResponse = await api.get(`/products/${subscription.value!.productId}`);
    if (productResponse.ok) {
      productDetails.value = await productResponse.json();
    }

    const orgName = orgDetails.value?.orgName ?? orgDetails.value?.name;
    const productName = productDetails.value?.name;
    trackView({
      id: subscription.value!.id,
      name: orgName ?? productName ?? `Subscription ${subscription.value!.id.slice(0, 8)}`,
      type: "subscriber",
      route: `/publisher/subscribers/${subscription.value!.id}`,
      description: orgName && productName ? `Subscribed to ${productName}` : productName,
      icon: "bi-people",
      metadata: {
        orgId: subscription.value!.orgId,
        productId: subscription.value!.productId,
      },
    });
  } catch (error) {
    console.error("Failed to load subscriber details:", error);
    hasError.value = true;
    if (error instanceof ConnectionError) {
      errorType.value = 'connection';
    } else {
      errorType.value = 'general';
    }
  } finally {
    loading.value = false;
  }
}

function formatDate(dateString: string | null): string {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function openEditModal() {
  if (subscription.value) {
    editForm.value.startDate = subscription.value.startDate.split("T")[0];
    editForm.value.endDate = subscription.value.endDate
      ? subscription.value.endDate.split("T")[0]
      : "";
    showEditModal.value = true;
  }
}

async function saveSubscription() {
  if (!subscription.value) return;

  try {
    loading.value = true;
    await subscriptionsApi.updateSubscription(subscriptionId.value, {
      startDate: editForm.value.startDate,
      endDate: editForm.value.endDate || null,
    });
    showEditModal.value = false;
    await loadSubscriberDetails();
  } catch (error) {
    console.error("Failed to update subscription:", error);
    alert("Failed to update subscription");
  } finally {
    loading.value = false;
  }
}

function removeSubscription() {
  showRemoveModal.value = true;
}

async function confirmRemoveSubscription() {
  showRemoveModal.value = false;
  try {
    loading.value = true;
    await subscriptionsApi.deactivateSubscription(subscriptionId.value);
    router.push({ name: "publisher-subscribers" });
  } catch (error) {
    console.error("Failed to remove subscription:", error);
  } finally {
    loading.value = false;
  }
}

function goBack() {
  router.back();
}
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Subscriber Details"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Publisher', href: '/publisher' },
          { label: 'Subscribers', href: '/publisher/subscribers' },
          { label: 'Details' },
        ]"
      >
        <template #actions>
          <button class="bb-btn-secondary-cta" @click="goBack">
            <i class="bi bi-arrow-left me-2"></i>Back
          </button>
        </template>
      </PageHeader>

      <!-- Loading State -->
      <div v-if="loading && !subscription" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error State -->
      <ErrorState
        v-else-if="hasError"
        :type="errorType"
        @retry="loadSubscriberDetails"
      />

      <!-- Content -->
      <div v-else-if="subscription">

        <!-- Organization & Product Info -->
        <div class="row g-4 mb-4">
          <div class="col-md-6">
            <div class="card h-100">
              <div class="card-body">
                <h2 class="card-title mb-3">
                  <i class="bi bi-building me-2"></i>Organization
                </h2>
                <div v-if="orgDetails">
                  <div class="mb-2">
                    <strong>Name:</strong>
                    <span class="ms-2">{{ orgDetails.name }}</span>
                  </div>
                  <div v-if="orgDetails.contactEmail" class="mb-2">
                    <strong>Contact Email:</strong>
                    <a :href="`mailto:${orgDetails.contactEmail}`" class="ms-2">
                      {{ orgDetails.contactEmail }}
                    </a>
                  </div>
                  <div v-if="orgDetails.website" class="mb-2">
                    <strong>Website:</strong>
                    <a :href="orgDetails.website" target="_blank" class="ms-2">
                      {{ orgDetails.website }}
                    </a>
                  </div>
                </div>
                <div v-else class="text-muted">Loading organization details...</div>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="card h-100">
              <div class="card-body">
                <h2 class="card-title mb-3">
                  <i class="bi bi-box-seam me-2"></i>Product
                </h2>
                <div v-if="productDetails">
                  <div class="mb-2">
                    <strong>Name:</strong>
                    <span class="ms-2">{{ productDetails.name }}</span>
                  </div>
                  <div v-if="productDetails.description" class="mb-2">
                    <strong>Description:</strong>
                    <p class="ms-2 mb-0 text-muted">{{ productDetails.description }}</p>
                  </div>
                </div>
                <div v-else class="text-muted">Loading product details...</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Subscription Details -->
        <div class="card mb-4">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2 class="card-title mb-0">
                <i class="bi bi-calendar-event me-2"></i>Subscription Details
              </h2>
              <button class="bb-btn-secondary-cta btn-sm" @click="openEditModal">
                <i class="bi bi-pencil me-2"></i>Edit
              </button>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <strong>Start Date:</strong>
                <span class="ms-2">{{ formatDate(subscription.startDate) }}</span>
              </div>
              <div class="col-md-6 mb-3">
                <strong>End Date:</strong>
                <span class="ms-2">{{ formatDate(subscription.endDate) }}</span>
              </div>
              <div class="col-md-6 mb-3">
                <strong>Subscribed At:</strong>
                <span class="ms-2">{{ formatDateTime(subscription.subscribedAt) }}</span>
              </div>
              <div class="col-md-6 mb-3">
                <strong>Subscription ID:</strong>
                <span class="ms-2 font-monospace small">{{ subscription.id }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="d-flex gap-2 flex-wrap">
          <button class="bb-btn-danger" @click="removeSubscription" :disabled="loading">
            <i class="bi bi-trash me-2"></i> Remove Subscription
          </button>
        </div>
      </div>

      <!-- Empty/Not Found State -->
      <div v-else class="text-center py-5 text-muted">
        <i class="bi bi-inbox fs-1 mb-3"></i>
        <p>No subscription found</p>
      </div>
    </PanelCard>

    <!-- Edit Modal -->
    <div
      class="modal fade"
      :class="{ show: showEditModal }"
      :style="{ display: showEditModal ? 'block' : 'none' }"
      tabindex="-1"
      @click.self="showEditModal = false"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title">Edit Subscription</h2>
            <button
              type="button"
              class="btn-close"
              @click="showEditModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="startDate" class="form-label">Start Date</label>
              <input
                type="date"
                class="form-control"
                id="startDate"
                v-model="editForm.startDate"
                required
              />
            </div>
            <div class="mb-3">
              <label for="endDate" class="form-label">End Date (Optional)</label>
              <input
                type="date"
                class="form-control"
                id="endDate"
                v-model="editForm.endDate"
              />
              <div class="form-text">Leave empty for no end date</div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="bb-btn-cancel"
              @click="showEditModal = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="bb-btn-primary-cta"
              @click="saveSubscription"
              :disabled="loading"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showEditModal"
      class="modal-backdrop fade show"
      @click="showEditModal = false"
    ></div>

    <!-- Remove Confirmation Modal -->
    <div
      class="modal fade"
      :class="{ show: showRemoveModal }"
      :style="{ display: showRemoveModal ? 'block' : 'none' }"
      tabindex="-1"
      @click.self="showRemoveModal = false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title text-danger">
              <i class="bi bi-exclamation-triangle me-2"></i>Remove Subscription
            </h2>
            <button type="button" class="btn-close" @click="showRemoveModal = false"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to remove the subscription for <strong>{{ orgDetails?.name }}</strong> from <strong>{{ productDetails?.name }}</strong>?</p>
            <p class="text-muted mb-0">This will permanently remove it from your subscriber list.</p>
          </div>
          <div class="modal-footer">
            <button class="bb-btn-cancel" @click="showRemoveModal = false">Cancel</button>
            <button class="btn btn-danger" @click="confirmRemoveSubscription">Remove</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showRemoveModal" class="modal-backdrop fade show" @click="showRemoveModal = false"></div>
  </PanelGrid>
</template>
